import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { SelectField } from '../menu/MenuUI';
import { ASSIGNABLE_ROLES, roleLabel } from './adminApi';

/**
 * Adding a member of staff, and correcting one.
 *
 * The phone's two sheets, side by side: full name, username, password, role to
 * add; the same minus a required password to update. Its wording, verbatim —
 * `Geli Magaca oo buuxa`, `Dooro Jagada`, `Ku Dar`, `Cusboonaysii` — including
 * the two validation messages its cubits raise.
 *
 * One deliberate difference: on update the role is shown but not editable. The
 * server's `updateUser` assigns a fixed list of fields and `role` is not one of
 * them, so the phone's role dropdown on that sheet changes nothing. A control
 * that silently does nothing is worse than no control.
 */

const COPY = {
  addTitle: 'Ku Dar User Cusub',
  editTitle: 'Cusboonaysii User',
  name: 'Geli Magaca oo buuxa',
  nameEdit: 'Magaca oo buuxa',
  username: 'Geli Username',
  usernameEdit: 'Username',
  password: 'Geli Password',
  passwordEdit: 'Password Cusub (Hadii aad rabto)',
  role: 'Dooro Jagada',
  add: 'Ku Dar',
  save: 'Cusboonaysii',
  // The phone's own validation messages, from its user cubits.
  allRequired: 'Fadlan buuxi xogta oo dhan',
  nameAndUsername: 'Fadlan geli magaca iyo username',
  failed: 'Could not save. Try again.',
  roleFixed: 'A role cannot be changed after the account is made.',
};

export default function UserDialog({ user, businessId, onClose, onSubmit }) {
  const editing = Boolean(user);

  const [name, setName] = React.useState(() => (user && user.name) || '');
  const [username, setUsername] = React.useState(() => (user && user.username) || '');
  const [password, setPassword] = React.useState('');
  const [role, setRole] = React.useState(() => (user && user.role) || 'waiter');

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (editing) {
      if (!name.trim() || !username.trim()) {
        setError(COPY.nameAndUsername);
        return;
      }
    } else if (!name.trim() || !username.trim() || !password) {
      setError(COPY.allRequired);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        user,
        payload: editing
          ? {
              name: name.trim(),
              username: username.trim(),
              // Left out entirely when blank: the server only touches the
              // password when one arrives, so blank means "leave it alone".
              ...(password ? { password, passwordConfirm: password } : {}),
            }
          : {
              name: name.trim(),
              username: username.trim(),
              password,
              passwordConfirm: password,
              role,
              business: businessId,
            },
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={410}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          <PillField
            name="user-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={editing ? COPY.nameEdit : COPY.name}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="user-username"
            value={username}
            onChange={(value) => {
              setUsername(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={editing ? COPY.usernameEdit : COPY.username}
            disabled={busy}
          />

          <PillField
            name="user-password"
            type="password"
            value={password}
            onChange={(value) => {
              setPassword(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={editing ? COPY.passwordEdit : COPY.password}
            disabled={busy}
          />

          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{COPY.role}</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 46,
                  padding: '0 16px',
                  background: T.sunk,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radiusPill,
                  fontSize: 14,
                  color: T.inkSoft,
                  boxSizing: 'border-box',
                }}
              >
                {roleLabel(user.role)}
              </div>
              <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>
                {COPY.roleFixed}
              </span>
            </div>
          ) : (
            <SelectField
              name="user-role"
              label={COPY.role}
              value={role}
              onChange={setRole}
              disabled={busy}
            >
              {ASSIGNABLE_ROLES.map((value) => (
                <option key={value} value={value}>
                  {roleLabel(value)}
                </option>
              ))}
            </SelectField>
          )}
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.save : COPY.add}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
